import React, { useCallback, useMemo } from 'react';
import ReactTable from 'react-table';

import CSVSummary from './CSVSummary';
import { UploadedCSV, QueryFilter } from './utils';
import useCheckPermissions from 'hooks/auth/useCheckPermission';
import TablePagination from '../../../components/ui/TablePagination';
import moment from 'moment/moment';
import CSVStatus from './CSVStatus';
import CSVAction from './CSVAction';
import { useCsvPerformanceStore } from './store';
import UserFilter from './UserFilter';
import UserWithAvatar from '../../../components/ui/UserWithAvatar';
import TemplateFilter from './TemplateFilter';
import EspFilter from './EspFilter';
import FilenameSearchBox from './FilenameSearchBox';
import { Tag, Button } from 'antd';

import styles from './DisplayCSVPerformance.module.css';
import CSVDownloader from './CSVDownloader';
import useHashState from 'hooks/useHashState';

type CSVPerformanceProps = {
  onRefresh: () => void,
  myId: number,
  myEmail: string,
  isLoading: boolean,
  uploadedCSV: UploadedCSV[],
  uploadedCSVCount: number,
  queryFilter: QueryFilter,
};

export default function CSVPerformance(props: CSVPerformanceProps) {
  const [selectedId, setSelectedId] = useHashState();
  const { userCheckPermission } = useCheckPermissions();
  const canInterruptCsv = userCheckPermission('mail.csv.interrupt');

  const { page, limit, setPage, setLimit } = useCsvPerformanceStore();

  const onClickDetails = useCallback(id => () => setSelectedId(id), [
    setSelectedId,
  ]);

  const onCloseDetailsModal = () => {
    setSelectedId(null);
  };

  const columnDefs = useMemo(() => {
    return [
      {
        Header: () => {
          return (
            <>
              <p style={{ marginBottom: 5 }}>Name</p>
              <FilenameSearchBox />
            </>
          );
        },
        id: 'originalname',
        accessor: data => {
          return <CSVDownloader csv={data} />;
        },
      },
      {
        Header: () => {
          return (
            <>
              <p style={{ marginBottom: 5 }}>Owner</p>
              <UserFilter />
            </>
          );
        },
        id: 'owner',
        accessor: data => {
          return <UserWithAvatar user={data.user} />;
        },
      },
      {
        Header: () => {
          return (
            <>
              <p style={{ marginBottom: 5 }}>Template</p>
              <TemplateFilter />
            </>
          );
        },
        id: 'template.id',
        maxWidth: 300,
        align: 'center',
        accessor: data => {
          if (data.isMultiCsv && data.multiTemplateIds) {
            const templateIds = data.multiTemplateIds.split(',');
            return (
              <div>
                {templateIds.map((templateId, index) => {
                  return (
                    <a
                      href={`/mail/email-templates?id=${templateId}`}
                      key={index}
                      style={{ paddingRight: 5 }}
                      target="_blank"
                    >
                      T{templateId}
                    </a>
                  );
                })}
              </div>
            );
          }
          if (!data.template) return 'N/A';
          return (
            <a
              href={`/mail/email-templates?id=${data.template.id}`}
              target="_blank"
            >
              {data.template.id} - {data.template.name}
            </a>
          );
        },
      },
      {
        Header: 'Template Category',
        id: 'template.category.id',
        accessor: data => {
          if (data.isMultiCsv && data.multiTemplateIds) return '';
          return data.template?.category?.name || 'N/A';
        },
      },
      {
        Header: () => {
          return (
            <>
              <p style={{ marginBottom: 5 }}>ESP</p>
              <EspFilter />
            </>
          );
        },
        id: 'esp',
        width: 100,
        accessor: data => {
          return data.esp === '-1' ? 'Automatic' : data.esp || '';
        },
      },
      {
        Header: 'Bypass Unsubscription',
        id: 'template.bypassUnsubscription',
        accessor: data => {
          if (!data.template) return <Tag color="blue">N/A</Tag>;
          return (
            <Tag color={data.template.bypassUnsubscription ? 'green' : 'red'}>
              {data.template.bypassUnsubscription ? 'Yes' : 'No'}
            </Tag>
          );
        },
      },
      {
        Header: 'Uploaded On',
        id: 'uploadedTime',
        accessor: data => {
          return moment(data.uploadedTime).format('YYYY-MM-DD HH:mm:ss');
        },
      },
      {
        Header: 'Status',
        id: 'status',
        accessor: data => {
          return <CSVStatus data={data} />;
        },
      },
      {
        Header: '',
        id: 'action',
        width: 250,
        accessor: data => {
          return (
            <CSVAction
              canInterruptMailSending={canInterruptCsv}
              data={data}
              myId={props.myId}
              queryFilter={props.queryFilter}
              onClickDetails={onClickDetails}
            />
          );
        },
      },
    ];
  }, [canInterruptCsv]);

  return (
    <div className={styles.container}>
      <Button
        icon="reload"
        loading={props.isLoading}
        style={{ marginBottom: 10 }}
        type="primary"
        onClick={props.onRefresh}
      >
        Refresh
      </Button>
      <ReactTable
        PaginationComponent={TablePagination}
        className={`-highlight ${styles.innerTable}`}
        columns={columnDefs}
        data={props.uploadedCSV}
        firstText={<i className="material-icons">first_page</i>}
        lastText={<i className="material-icons">last_page</i>}
        manual
        minRows={0}
        nextText={<i className="material-icons">chevron_right</i>}
        page={page - 1}
        pageSize={limit}
        pageSizeOptions={[5, 10, 15, 20]}
        pages={parseInt(props.uploadedCSVCount / limit) + 1}
        previousText={<i className="material-icons">chevron_left</i>}
        rowsSelectorText="Rows per page"
        showPageJump={false}
        showPaginationBottom
        sortable={false}
        onPageChange={nextPage => setPage(nextPage + 1)}
        onPageSizeChange={setLimit}
      />
      {!!selectedId && (
        <CSVSummary
          data={
            selectedId ? props.uploadedCSV.find(v => v.id === +selectedId) : {}
          }
          myEmail={props.myEmail}
          myId={props.myId}
          queryFilter={props.queryFilter}
          selectedId={+selectedId}
          show
          onClickDetails={onClickDetails}
          onClose={onCloseDetailsModal}
        />
      )}
    </div>
  );
}
